import React from 'react';
import { View, Text, Pressable, Linking, StyleSheet } from 'react-native';
import BaseModal, { modalStyles, HoverButton } from './BaseModal';
import { FontAwesome6 } from '@expo/vector-icons';
import MenuListItem from '../ui/MenuListItem';
import ModalStickyFooter from '../ui/ModalStickyFooter';
import { colors, typography } from '../../styles/theme';
import { handleBuyTreat } from '../../utils/purchase';

interface MenuModalProps {
  visible: boolean;
  onClose: () => void;
  onViewResults: () => void;
  onRestart: () => void;
  onExportArt: () => void;
  onCreateCustom: () => void;
  onToggleMute: () => void;
  onToggleEasyMode: () => void;
  onHowToPlay: () => void;
  onBrowsePacks: () => void;
  onWhyPiclo: () => void;
  onTermsOfUse: () => void;
  onPrivacyPolicy: () => void;
  onContact: () => void;
  isMuted: boolean;
  isEasyMode: boolean;
  isCompleted: boolean;
}

export default function MenuModal({
  visible, onClose, onViewResults, onRestart,
  onExportArt, onCreateCustom, onToggleMute, onToggleEasyMode, onHowToPlay, onBrowsePacks, onWhyPiclo,
  onTermsOfUse, onPrivacyPolicy, onContact,
  isMuted, isEasyMode, isCompleted
}: MenuModalProps) {
  return (
    <BaseModal
      visible={visible}
      onClose={onClose}
      header={<Text style={modalStyles.headerText}>Menu</Text>}
      footer={(
        <ModalStickyFooter>
          <HoverButton
            style={modalStyles.secondaryButton}
            textStyle={modalStyles.secondaryButtonText}
            onPress={onClose}
          >
            Close
          </HoverButton>
        </ModalStickyFooter>
      )}
    >

      <Text style={styles.sectionHeader}>Explore</Text>
      <View style={styles.listGroup}>
        {isCompleted && (
          <MenuListItem
            icon={<FontAwesome6 name="chart-simple" size={18} color={colors.text} solid />}
            label="View Results"
            onPress={onViewResults}
          />
        )}

        <MenuListItem
          icon={<FontAwesome6 name="palette" size={18} color={colors.text} solid />}
          label="Browse Puzzle Packs"
          onPress={onBrowsePacks}
        />

        <MenuListItem
          icon={<FontAwesome6 name="paintbrush" size={18} color={colors.text} solid />}
          label="Create a Custom Puzzle"
          onPress={onCreateCustom}
        />

        <MenuListItem
          icon={<FontAwesome6 name="circle-question" size={18} color={colors.text} solid />}
          label="How To Play"
          onPress={onHowToPlay}
          isLast
        />
      </View>

      <Text style={styles.sectionHeader}>Options</Text>
      <View style={styles.listGroup}>
        <MenuListItem
          icon={<FontAwesome6 name="rotate-right" size={18} color={colors.text} solid />}
          label="Restart Puzzle"
          onPress={onRestart}
        />

        <MenuListItem
          icon={<FontAwesome6 name="arrow-up-from-bracket" size={18} color={colors.text} solid />}
          label="Export Art"
          onPress={onExportArt}
        />

        <MenuListItem
          icon={<FontAwesome6 name={isMuted ? "volume-xmark" : "volume-high"} size={18} color={colors.text} solid />}
          label={isMuted ? "Unmute Audio" : "Mute Audio"}
          onPress={onToggleMute}
        />

        <MenuListItem
          icon={
            <View>
              <FontAwesome6 name="water" size={18} color={colors.text} solid />
              {isEasyMode && (
                <View style={{ position: 'absolute', bottom: -4, right: -6, backgroundColor: '#ffffff', borderRadius: 10 }}>
                  <FontAwesome6 name="circle-check" size={14} color={colors.text} solid />
                </View>
              )}
            </View>
          }
          label={isEasyMode ? "Blurred Mode On" : "Blurred Mode Off"}
          onPress={onToggleEasyMode}
          isLast
        />

        {/* <MenuListItem
          icon={<FontAwesome6 name="heart" size={18} color={colors.text} solid />}
          label="Why Piclo?"
          onPress={onWhyPiclo}
          isLast
        /> */}
      </View>

      {/* The Footer goes inside the component, right above the Close button */}
      <View style={styles.footer}>
        <Pressable onPress={handleBuyTreat}>
          <Text style={styles.link}>Buy Piccolo a treat 🦴</Text>
        </Pressable>
        <Text style={styles.footerText}>
          Created by <Text style={styles.link} onPress={() => Linking.openURL('https://jscottk.net')}>Scott Kuehnert</Text>.
        </Text>

        <View style={styles.legalLinks}>
          <Text style={styles.link} onPress={onTermsOfUse}>Terms of Use</Text>
          <Text style={styles.bullet}>•</Text>
          <Text style={styles.link} onPress={onPrivacyPolicy}>Privacy Policy</Text>
          <Text style={styles.bullet}>•</Text>
          <Text style={styles.link} onPress={() => Linking.openURL('mailto:support@piclo.art')}>Contact</Text>
        </View>
      </View>

    </BaseModal>
  );
}

// The styles must be wrapped in StyleSheet.create()
const styles = StyleSheet.create({
  listGroup: {
    width: '100%',
    marginBottom: 16,
  },
  sectionHeader: {
    ...typography.bodyBold,
    color: colors.textMuted,
    fontSize: 13,
    letterSpacing: 0.6,
    textTransform: 'uppercase',
    marginBottom: 0,
    marginTop: 10,
    fontWeight: 'bold'
  },
  footer: {
    marginTop: 24,
    marginBottom: 18,
    alignItems: 'center',
    gap: 4
  },
  footerText: {
    ...typography.body,
    fontSize: 16,
    color: colors.textMuted,
  },
  link: {
    ...typography.body,
    fontSize: 16,
    color: colors.textMuted,
    fontWeight: 'normal',
    textDecorationLine: 'underline',
  },
  legalLinks: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    marginTop: 0,
  },
  bullet: {
    ...typography.body,
    fontSize: 14,
    color: colors.textMuted,
  }
});
